import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { api, ApiError } from '../api/client';
import {
    Button, Card, Field, Select, Loading, ErrorNote, PageHeader, toast
} from '../components/ui';

interface Outcome {
    created: number;
    updated: number;
    errors: string[];
    links?: { candidate: string; url: string }[];
}

/**
 * Two imports behind one screen: candidates, and scheduling interviews in bulk.
 *
 * Both report per row. A recruiter with one bad row in two hundred gets the other hundred and
 * ninety-nine plus a list of what to fix, rather than being sent away to correct a file.
 */
export function Import() {
    const [params, setParams] = useSearchParams();
    const mode = params.get('mode') === 'interviews' ? 'interviews' : 'candidates';

    const [file, setFile] = useState<File | null>(null);
    const [templateId, setTemplateId] = useState('');
    const [outcome, setOutcome] = useState<Outcome | null>(null);
    const [error, setError] = useState<ApiError | null>(null);
    const [busy, setBusy] = useState(false);

    const templates = useQuery({
        queryKey: ['templates'],
        queryFn: () => api.get<{ id: number; name: string; isDefault: boolean }[]>(
            '/api/admin/templates'),
        enabled: mode === 'interviews'
    });

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!file) return;

        setBusy(true);
        setError(null);
        setOutcome(null);

        const body = new FormData();
        body.append('file', file);

        const url = mode === 'candidates'
            ? '/api/admin/candidates/import'
            : `/api/admin/interviews/bulk?templateId=${templateId || 0}`;

        try {
            // Multipart, so this bypasses the JSON client on purpose.
            const response = await fetch(url, { method: 'POST', body, credentials: 'same-origin' });
            const payload = await response.json();

            if (!payload.success) {
                throw new ApiError(payload.message, response.status, payload.errors ?? null);
            }

            setOutcome(payload.data);
            toast('Import finished.');
        } catch (caught) {
            setError(caught instanceof ApiError
                ? caught
                : new ApiError((caught as Error).message, 500, null));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-3xl space-y-6">
            {/* The heading names the page; the buttons below choose which of its two jobs you are
                doing, and the card under them repeats the chosen one as its section title. */}
            <PageHeader
                title="Import from a file"
                lede="Bring candidates in from a spreadsheet, or schedule a batch of interviews for people who are already here."
            />

            <Card>
                <div className="flex gap-2">
                    {(['candidates', 'interviews'] as const).map((option) => (
                        <button
                            key={option}
                            onClick={() => {
                                setParams(option === 'candidates' ? {} : { mode: option });
                                setOutcome(null);
                                setError(null);
                            }}
                            className={`rounded-lg px-3 py-1.5 text-sm transition ${
                                mode === option
                                    ? 'bg-[var(--brand-primary)] text-white'
                                    : 'border border-slate-300 text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                            {option === 'candidates' ? 'Import candidates' : 'Schedule many interviews'}
                        </button>
                    ))}
                </div>
            </Card>

            <Card title={mode === 'candidates' ? 'Import candidates' : 'Schedule many interviews'}>
                <form onSubmit={submit} className="space-y-4">
                    {error && <ErrorNote message={error.message} />}

                    {/* The header row, the update-by-reference rule and the row limit are all stated
                        once, below, next to the sample file and the worked example. They used to be
                        summarised here as well, which meant the page said the same thing twice before
                        the reader reached the part that actually helps. */}

                    {mode === 'interviews' && (
                        <Field label="Template for rows that do not name one">
                            {templates.isLoading ? <Loading /> : (
                                <Select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                                    <option value="">Use the default template</option>
                                    {(templates.data ?? []).map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.name}{t.isDefault ? ' (default)' : ''}
                                        </option>
                                    ))}
                                </Select>
                            )}
                        </Field>
                    )}

                    {/* The original offered a file to start from rather than only describing the
                        format. Someone staring at a spreadsheet needs the former. */}
                    <div className="rounded-lg border p-3.5" style={{ borderColor: 'var(--app-border)', background: 'var(--app-bg)' }}>
                        <strong className="block text-sm" style={{ color: 'var(--app-heading)' }}>
                            Not sure what the file should look like?
                        </strong>
                        <div className="mt-2 flex flex-wrap items-center gap-3">
                            <a
                                href={mode === 'candidates' ? '/samples/candidates-sample.csv' : '/samples/interviews-sample.csv'}
                                download
                            >
                                <Button type="button" variant="secondary">
                                    <i className="bi bi-download" aria-hidden="true" />
                                    Download the sample file
                                </Button>
                            </a>
                            <span className="text-sm" style={{ color: 'var(--text-muted-safe)' }}>
                                Fill it in and upload it back — as CSV, or saved from Excel as .xlsx.
                            </span>
                        </div>
                    </div>

                    <div>
                        <p className="text-sm">The first row must be the column names:</p>
                        <pre
                            className="app-mono mt-1.5 overflow-x-auto rounded-lg p-3"
                            style={{ background: 'var(--app-bg)', color: 'var(--app-text)' }}
                        >{mode === 'candidates'
                            ? 'FullName,Email,Mobile,ExternalRef,Position\nAsha Menon,asha@example.com,9876543210,MRF-1042,Sales Associate'
                            : 'FullName,Email,ExternalRef,InterviewFormat,ScheduledAt\nAsha Menon,asha@example.com,MRF-1042,,20-08-2026 10:30'}</pre>
                    </div>

                    <ul className="list-disc space-y-1.5 pl-5 text-sm">
                        {mode === 'candidates' ? (
                            <>
                                <li><strong>FullName</strong> is required; everything else is optional.</li>
                                <li>
                                    Rows carrying an <strong>ExternalRef</strong> that already exists update
                                    that candidate instead of creating a duplicate, so the same feed can be
                                    sent again safely.
                                </li>
                                <li>Maximum 2000 rows per file.</li>
                            </>
                        ) : (
                            <>
                                <li>
                                    Each row needs an <strong>Email</strong> or a <strong>reference</strong>
                                    {' '}that already belongs to a candidate. Nobody new is created here —
                                    add people on the Candidates page first.
                                </li>
                                <li><strong>InterviewFormat</strong> and <strong>ScheduledAt</strong> are optional.</li>
                                <li>
                                    Write dates <strong>day first</strong>: <span className="app-mono">20-08-2026 10:30</span>,
                                    with slashes if you prefer. The time is taken exactly as you write it. In
                                    Excel, a cell already formatted as a date is read from the cell itself.
                                </li>
                                <li>
                                    A candidate who already has an interview waiting to be taken or waiting to
                                    be approved is skipped, and the reason is shown to you afterwards.
                                </li>
                                <li>Up to 200 rows per file.</li>
                            </>
                        )}
                    </ul>

                    <Field label="CSV or Excel file">
                        <input
                            type="file"
                            accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        />
                    </Field>

                    <div className="flex gap-2">
                        <Button type="submit" busy={busy} disabled={!file}>Import</Button>
                        <Link to={mode === 'candidates' ? '/candidates' : '/interviews'}>
                            <Button type="button" variant="secondary">Cancel</Button>
                        </Link>
                    </div>
                </form>
            </Card>

            {outcome && (
                <Card title="What happened">
                    <p className="text-sm text-slate-700">
                        {mode === 'candidates'
                            ? `${outcome.created} added, ${outcome.updated} updated.`
                            : `${outcome.created} interview(s) scheduled.`}
                        {outcome.errors.length > 0 && ` ${outcome.errors.length} row(s) skipped.`}
                    </p>

                    {outcome.links && outcome.links.length > 0 && (
                        <div className="mt-4">
                            <h3 className="mb-1 text-sm font-medium text-slate-700">Interview links</h3>
                            {/* Shown once, because only a hash of each token is stored. A row whose
                                invitation could not be emailed is otherwise unrecoverable. */}
                            <p className="mb-2 text-xs text-slate-500">
                                Copy these now — they cannot be shown again. Resending an interview
                                mints a new link and retires the old one.
                            </p>
                            <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg bg-slate-50 p-3">
                                {outcome.links.map((link) => (
                                    <p key={link.url} className="break-all text-xs text-slate-700">
                                        <span className="font-medium">{link.candidate}</span> — {link.url}
                                    </p>
                                ))}
                            </div>
                            <Button
                                variant="secondary"
                                className="mt-2"
                                onClick={async () => {
                                    await navigator.clipboard.writeText(
                                        outcome.links!.map((l) => `${l.candidate}\t${l.url}`).join('\n'));
                                    toast('Copied all links.');
                                }}
                            >
                                Copy all
                            </Button>
                        </div>
                    )}

                    {outcome.errors.length > 0 && (
                        <div className="mt-4">
                            <h3 className="mb-1 text-sm font-medium text-slate-700">Rows that were skipped</h3>
                            <ul className="space-y-1 rounded-lg bg-amber-50 p-3">
                                {outcome.errors.map((message) => (
                                    <li key={message} className="text-xs text-amber-900">{message}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Card>
            )}
        </div>
    );
}
